import React, {useMemo} from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Checkbox,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import {ErrorOutline, KeyboardArrowDown} from '@mui/icons-material';
import ExpandableText from 'src/components/ExpandableText';
import {FunctionalEnrichmentSampleGroup} from 'src/utils/chip';

const GROUP_TABLE_COLUMNS = '44px repeat(4, minmax(0, 1fr)) 90px 48px';
const DISABLED_GROUP_TOOLTIP = 'This group cannot be selected because it is incomplete or does not match the selected default analysis.';
const TEXT_PREVIEW_MAX_CHARS = 40;

interface SampleGroupTableProps {
    groups: FunctionalEnrichmentSampleGroup[];
    selectedSampleIds: string[];
    searchTerm: string;
    selectionDisabled: boolean;
    onToggleGroup: (group: FunctionalEnrichmentSampleGroup) => void;
    onToggleAll: (groups: FunctionalEnrichmentSampleGroup[]) => void;
}

const GroupSamples = ({title, samples}: {title: string; samples: FunctionalEnrichmentSampleGroup['samples']}) => {
    if (samples.length === 0) return null;
    return (
        <Box sx={{mb: 2, '&:last-child': {mb: 0}}}>
            <Typography variant="subtitle2" sx={{mb: 1}}>
                {title}
            </Typography>
            <Table size="small" sx={{tableLayout: 'fixed'}}>
                <TableHead>
                    <TableRow
                        sx={{
                            '& .MuiTableCell-root': {
                                bgcolor: 'surface-container-highest'
                            }
                        }}>
                        <TableCell sx={{width: '60%'}}>Sample name</TableCell>
                        <TableCell sx={{width: '20%'}}>Replicate</TableCell>
                        <TableCell sx={{width: '20%'}}>Sample type</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {samples.map(sample => (
                        <TableRow key={sample.id} sx={{bgcolor: 'on-primary'}}>
                            <TableCell>
                                <ExpandableText value={sample.name} maxChars={TEXT_PREVIEW_MAX_CHARS} />
                            </TableCell>
                            <TableCell>{sample.other_categories.Replicate}</TableCell>
                            <TableCell>{sample.other_categories.SampleType}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Box>
    );
};

const SampleGroupTable = ({
    groups,
    selectedSampleIds,
    searchTerm,
    selectionDisabled,
    onToggleGroup,
    onToggleAll
}: SampleGroupTableProps) => {
    const visibleGroups = useMemo(() => {
        const search = searchTerm.trim().toLowerCase();
        const matchingGroups = search
            ? groups.filter(group =>
                  [
                      ...Object.values(group.group_metadata),
                      ...group.samples.map(sample => sample.name),
                      ...group.controls.map(sample => sample.name)
                  ].some(value => value.toLowerCase().includes(search))
              )
            : groups;

        return [...matchingGroups].sort((a, b) => Number(a.disabled) - Number(b.disabled));
    }, [groups, searchTerm]);

    const selectableGroups = visibleGroups.filter(group => !group.disabled);
    const selectedIds = useMemo(() => new Set(selectedSampleIds), [selectedSampleIds]);
    const isSelected = (group: FunctionalEnrichmentSampleGroup) => group.samples.every(sample => selectedIds.has(sample.id));
    const allSelected = selectableGroups.length > 0 && selectableGroups.every(isSelected);
    const selectedGroupCount = groups.filter(isSelected).length;

    return (
        <>
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{mb: 1}}>
                <Box display="flex" alignItems="center">
                    <Checkbox
                        checked={allSelected}
                        disabled={selectionDisabled || selectableGroups.length === 0}
                        onChange={() => onToggleAll(selectableGroups)}
                        inputProps={{'aria-label': 'Select all available sample groups'}}
                    />
                    <Typography>Select all available groups</Typography>
                </Box>
                <Typography>
                    <b>{selectedGroupCount}</b> of <b>{groups.filter(group => !group.disabled).length}</b> groups selected
                </Typography>
            </Box>

            <Box sx={{border: '1px solid', borderColor: 'outline', borderRadius: '5px', maxHeight: 600, overflowY: 'auto'}}>
                <Box
                    display="grid"
                    gridTemplateColumns={GROUP_TABLE_COLUMNS}
                    columnGap={2}
                    width="100%"
                    sx={{px: 2, py: 1, bgcolor: 'surface-container-highest'}}>
                    <span />
                    <Typography variant="caption">FACTOR</Typography>
                    <Typography variant="caption">TISSUE</Typography>
                    <Typography variant="caption">CONDITION</Typography>
                    <Typography variant="caption">TREATMENT</Typography>
                    <Typography variant="caption">GROUP SIZE</Typography>
                    <span />
                </Box>

                {visibleGroups.map(group => {
                    const selected = isSelected(group);
                    const checkbox = (
                        <Checkbox
                            checked={selected}
                            disabled={selectionDisabled || group.disabled}
                            onClick={event => event.stopPropagation()}
                            onChange={() => onToggleGroup(group)}
                            inputProps={{'aria-label': `Select ${group.group_metadata.Factor} sample group`}}
                        />
                    );

                    return (
                        <Accordion
                            key={group.key}
                            disableGutters
                            sx={{
                                boxShadow: 'none',
                                bgcolor: group.disabled ? 'action.hover' : selected ? '#dbdce273' : 'on-primary',
                                color: group.disabled ? 'text.secondary' : 'inherit',
                                borderTop: '1px solid',
                                borderColor: 'outline',
                                '&:before': {display: 'none'}
                            }}>
                            <AccordionSummary role="presentation" tabIndex={-1} aria-expanded={undefined}>
                                <Box
                                    display="grid"
                                    gridTemplateColumns={GROUP_TABLE_COLUMNS}
                                    columnGap={2}
                                    width="100%"
                                    alignItems="center">
                                    {group.disabled ? (
                                        <Tooltip title={DISABLED_GROUP_TOOLTIP}>
                                            <span>{checkbox}</span>
                                        </Tooltip>
                                    ) : (
                                        checkbox
                                    )}
                                    <ExpandableText value={group.group_metadata.Factor} maxChars={TEXT_PREVIEW_MAX_CHARS} />
                                    <ExpandableText value={group.group_metadata.Tissue} maxChars={TEXT_PREVIEW_MAX_CHARS} />
                                    <ExpandableText value={group.group_metadata.Condition} maxChars={TEXT_PREVIEW_MAX_CHARS} />
                                    <ExpandableText value={group.group_metadata.Treatment} maxChars={TEXT_PREVIEW_MAX_CHARS} />
                                    <Typography>
                                        {group.samples.length} {group.samples.length === 1 ? 'sample' : 'samples'}
                                    </Typography>
                                    <Box display="flex" justifyContent="center">
                                        <IconButton aria-label={`Toggle ${group.group_metadata.Factor} sample group details`} size="small">
                                            <KeyboardArrowDown
                                                sx={{
                                                    color: group.disabled ? 'text.secondary' : 'inherit',
                                                    transition: 'transform 150ms',
                                                    '.Mui-expanded &': {transform: 'rotate(180deg)'}
                                                }}
                                            />
                                        </IconButton>
                                    </Box>
                                    {group.disabled && (
                                        <Box
                                            sx={{
                                                gridColumn: '2 / 7',
                                                pb: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                color: 'error.main'
                                            }}>
                                            <ErrorOutline sx={{fontSize: '18px'}} />
                                            <Typography variant="caption" color="inherit">
                                                {group.disabledReason}
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails
                                sx={{
                                    px: 3,
                                    py: 2,
                                    bgcolor: 'on-primary',
                                    borderTop: '1px solid',
                                    borderColor: 'outline-variant'
                                }}>
                                <GroupSamples title="IP samples" samples={group.samples} />
                                <GroupSamples title="Controls" samples={group.controls} />
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>

            {visibleGroups.length === 0 && <Typography sx={{py: 3}}>No sample groups match your search.</Typography>}
            {groups.some(group => group.disabled) && (
                <Alert severity="warning" sx={{mt: 2}}>
                    Some groups cannot be selected because they do not match the selected default analysis. You can select any remaining
                    groups, or choose a different default analysis if you need one of the disabled groups. If no existing analysis matches,
                    run a new default analysis with the current samples.
                </Alert>
            )}
        </>
    );
};

export default SampleGroupTable;
